import { useQuery } from "convex/react";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useParams } from "react-router";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Input,
  Label,
} from "@repo/ui";
import { useCandidateAuthStore } from "@repo/auth";
import { AlertCircle, Lock } from "lucide-react";

export function ParcoursGuard() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { token, setToken, setFirstName, setEmail, setParcoursId } =
    useCandidateAuthStore();

  // Token depuis l'état de navigation (création libre-service) ou depuis le store
  const preAuthToken = (location.state as { token?: string } | null)?.token;
  const [currentToken, setCurrentToken] = useState<string | undefined>(
    preAuthToken || token || undefined,
  );
  const [error, setError] = useState<string | undefined>();

  // Métadonnées d'abord, sans authentification, pour savoir si le parcours est anonyme
  const metadata = useQuery(
    api.parcoursEvaluations.obtenirMetadonneesParcours,
    id ? { parcoursId: id as Id<"parcoursEvaluations"> } : "skip",
  );

  const estAnonyme = metadata?.anonyme === true;

  // Query de vérification d'accès
  const result = useQuery(
    api.parcoursEvaluations.verifierAccesParcours,
    id && currentToken
      ? {
          parcoursId: id as Id<"parcoursEvaluations">,
          identifiant: currentToken,
        }
      : "skip",
  );

  // Sync currentToken depuis le store après réhydratation Zustand (async)
  useEffect(() => {
    if (!currentToken && token) {
      setCurrentToken(token);
    }
  }, [token, currentToken]);

  // Met à jour les valeurs stockées quand la validation réussit
  useEffect(() => {
    if (result && result.statut === "ok" && id && currentToken) {
      setToken(currentToken);
      setParcoursId(id);
      if (result.prenomCandidat) setFirstName(result.prenomCandidat);
      if (result.emailCandidat) setEmail(result.emailCandidat);
      setError(undefined);
    }
  }, [
    result,
    id,
    currentToken,
    setToken,
    setParcoursId,
    setFirstName,
    setEmail,
  ]);

  // Soumission de la modale
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const value = formData.get("identifiant") as string;
    if (value?.trim()) {
      setError(undefined);
      setCurrentToken(value.trim());
    }
  };

  // Aucun identifiant dans l'URL
  if (!id) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-destructive">Lien invalide</h1>
          <p className="mt-2 text-muted-foreground">
            Le lien d'accès est invalide ou incomplet.
          </p>
        </div>
      </div>
    );
  }

  // Chargement des métadonnées
  if (metadata === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const label = estAnonyme ? "Token d'accès" : "Nom de famille";
  const placeholder = estAnonyme
    ? "Entrez votre token (ex: abc-1234)"
    : "Entrez votre nom de famille";
  const autoComplete = estAnonyme ? "off" : "family-name";

  // Pas encore de token : on affiche la modale
  if (!currentToken) {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
              <Lock className="h-6 w-6 text-primary" />
            </div>
            <CardTitle>Vérification d'identité</CardTitle>
            <CardDescription>
              {estAnonyme
                ? "Pour accéder à votre parcours d'évaluations, veuillez saisir le token qui vous a été communiqué."
                : "Pour accéder à votre parcours d'évaluations, veuillez saisir votre nom de famille tel qu'il a été enregistré."}
            </CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifiant">{label}</Label>
                <Input
                  id="identifiant"
                  name="identifiant"
                  type="text"
                  placeholder={placeholder}
                  autoFocus
                  autoComplete={autoComplete}
                />
              </div>
            </CardContent>

            <CardFooter className="pt-6">
              <Button type="submit" className="w-full">
                Accéder au parcours
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    );
  }

  // État de chargement
  if (result === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Erreur remontée par la query
  if (result.statut === "erreur") {
    return (
      <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>Erreur d'accès</CardTitle>
            <CardDescription>{result.message}</CardDescription>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="identifiant">{label}</Label>
                <Input
                  id="identifiant"
                  name="identifiant"
                  type="text"
                  placeholder={placeholder}
                  autoFocus
                  autoComplete={autoComplete}
                />
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-surface bg-destructive/10 text-destructive text-sm">
                  <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <p>{error}</p>
                </div>
              )}
            </CardContent>

            <CardFooter className="pt-6">
              <Button type="submit" className="w-full">
                Réessayer
              </Button>
            </CardFooter>
          </form>
        </Card>
      </div>
    );
  }

  return <Outlet />;
}
